/**
 * EMZI NEXUS V6.4 - Live Studio V2
 * Step 10: Review & Submit for Approval
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { EMZI_SPACING, EMZI_RADIUS } from '../../../styles/emziCI';
import * as api from '../../../api/liveStudioV2';

const Step10ReviewSubmit = ({ sessionId, token, sessionData, onComplete, onBack, setError, setLoading }) => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  // State
  const [projectName, setProjectName] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submissionId, setSubmissionId] = useState(null);

  const getText = (key) => {
    const translations = {
      en: {
        title: 'Review & Submit',
        subtitle: 'Final review before submission',
        scriptSummary: 'Script Summary',
        soaSummary: 'SOA Summary',
        projectNameLabel: 'Project Name (Optional)',
        projectNamePlaceholder: 'e.g., TikTok Live - Product Launch',
        notesLabel: 'Additional Notes (Optional)',
        notesPlaceholder: 'Any notes for supervisor...',
        submitBtn: '🚀 Submit for Approval',
        submittingBtn: 'Submitting...',
        successTitle: '✅ Submission Successful!',
        successMsg: 'Your live script has been submitted for supervisor approval.',
        submissionIdLabel: 'Submission ID',
        viewSubmissionsBtn: 'View My Submissions',
        createNewBtn: 'Create New Session',
        sections: 'Sections',
        ctas: 'CTAs',
        avgScore: 'Avg Score',
        passed: 'Passed'
      },
      ms: {
        title: 'Semak & Hantar',
        subtitle: 'Semakan akhir sebelum penghantaran',
        scriptSummary: 'Ringkasan Skrip',
        soaSummary: 'Ringkasan SOA',
        projectNameLabel: 'Nama Projek (Pilihan)',
        projectNamePlaceholder: 'cth., TikTok Live - Pelancaran Produk',
        notesLabel: 'Nota Tambahan (Pilihan)',
        notesPlaceholder: 'Sebarang nota untuk supervisor...',
        submitBtn: '🚀 Hantar untuk Kelulusan',
        submittingBtn: 'Menghantar...',
        successTitle: '✅ Penghantaran Berjaya!',
        successMsg: 'Skrip live anda telah dihantar untuk kelulusan supervisor.',
        submissionIdLabel: 'ID Penghantaran',
        viewSubmissionsBtn: 'Lihat Penghantaran Saya',
        createNewBtn: 'Buat Sesi Baharu',
        sections: 'Seksyen',
        ctas: 'CTA',
        avgScore: 'Skor Purata',
        passed: 'Lulus'
      },
      bm: {
        title: 'Semak & Hantar',
        subtitle: 'Semakan akhir sebelum penghantaran',
        scriptSummary: 'Ringkasan Skrip',
        soaSummary: 'Ringkasan SOA',
        projectNameLabel: 'Nama Projek (Pilihan)',
        projectNamePlaceholder: 'cth., TikTok Live - Pelancaran Produk',
        notesLabel: 'Nota Tambahan (Pilihan)',
        notesPlaceholder: 'Sebarang nota untuk supervisor...',
        submitBtn: '🚀 Hantar untuk Kelulusan',
        submittingBtn: 'Menghantar...',
        successTitle: '✅ Penghantaran Berjaya!',
        successMsg: 'Skrip live anda telah dihantar untuk kelulusan supervisor.',
        submissionIdLabel: 'ID Penghantaran',
        viewSubmissionsBtn: 'Lihat Penghantaran Saya',
        createNewBtn: 'Buat Sesi Baharu',
        sections: 'Seksyen',
        ctas: 'CTA',
        avgScore: 'Skor Purata',
        passed: 'Lulus'
      },
      urban: {
        title: 'Review & Submit',
        subtitle: 'Final review before submission',
        scriptSummary: 'Script Summary',
        soaSummary: 'SOA Summary',
        projectNameLabel: 'Project Name (Optional)',
        projectNamePlaceholder: 'e.g., TikTok Live - Product Launch',
        notesLabel: 'Additional Notes (Optional)',
        notesPlaceholder: 'Any notes untuk supervisor...',
        submitBtn: '🚀 Submit for Approval',
        submittingBtn: 'Submitting...',
        successTitle: '✅ Submission Successful!',
        successMsg: 'Live script korang dah submitted untuk supervisor approval.',
        submissionIdLabel: 'Submission ID',
        viewSubmissionsBtn: 'View My Submissions',
        createNewBtn: 'Create New Session',
        sections: 'Sections',
        ctas: 'CTAs',
        avgScore: 'Avg Score',
        passed: 'Passed'
      }
    };
    return translations[language]?.[key] || translations.en[key];
  };

  // Get stats from session data
  const script = sessionData?.script;
  const soaSummary = sessionData?.soaSummary;
  const soaAnswers = sessionData?.soaAnswers || [];

  const getSOAStats = () => {
    if (soaSummary) {
      return {
        avgScore: soaSummary.average_score,
        passed: soaSummary.passed,
        total: soaSummary.total_questions
      };
    }
    // Calculate from answers
    const scores = soaAnswers.map(a => a.score || 0);
    const avgScore = scores.length > 0 ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1) : 0;
    const passed = soaAnswers.filter(a => a.passed).length;
    return { avgScore, passed, total: soaAnswers.length };
  };

  const soaStats = getSOAStats();

  // Submit for approval
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    
    try {
      const result = await api.submitLiveForApproval(
        token,
        sessionId,
        projectName || `Live Session ${sessionId}`,
        notes
      );
      
      setSubmissionId(result.submission_id);
      setIsSubmitted(true);
      
      // Clear localStorage
      localStorage.removeItem('ls_v2_session_id');
      localStorage.removeItem(`ls_v2_${sessionId}_data`);
      localStorage.removeItem(`ls_v2_${sessionId}_step`);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success screen
  if (isSubmitted) {
    return (
      <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
        <div style={{ marginBottom: EMZI_SPACING.xl }}>
          <div style={{ fontSize: '80px', marginBottom: '20px' }}>🎉</div>
          <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#fff', marginBottom: '12px' }}>
            {getText('successTitle')}
          </h1>
          <p style={{ fontSize: '16px', color: 'rgba(255,255,255,0.9)' }}>
            {getText('successMsg')}
          </p>
        </div>

        <Card style={{ marginBottom: EMZI_SPACING.lg, borderRadius: EMZI_RADIUS.md }}>
          <CardContent style={{ padding: '24px' }}>
            <div style={{ fontSize: '12px', color: '#999', marginBottom: '4px' }}>
              {getText('submissionIdLabel')}
            </div>
            <div style={{ 
              fontSize: '18px', 
              fontWeight: '700', 
              color: '#0e67a2',
              fontFamily: 'monospace',
              background: '#E8F4FF',
              padding: '12px',
              borderRadius: '8px'
            }}>
              {submissionId}
            </div>
          </CardContent>
        </Card>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <button
            data-testid="launch-teleprompter-btn"
            onClick={() => navigate(`/studio/live-v2/teleprompter?session=${sessionId}`)}
            style={{
              padding: '16px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              borderRadius: '10px',
              color: '#fff',
              fontSize: '16px',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
            }}
          >
            Launch Teleprompter
          </button>
          <button
            data-testid="view-submissions-btn"
            onClick={() => navigate('/studios')}
            style={{
              padding: '14px',
              background: '#0e67a2',
              border: 'none',
              borderRadius: '10px',
              color: '#fff',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            {getText('viewSubmissionsBtn')}
          </button>
          <button
            data-testid="create-new-session-btn"
            onClick={() => {
              window.location.reload();
            }}
            style={{
              padding: '14px',
              background: '#fff',
              border: '2px solid #0e67a2',
              borderRadius: '10px',
              color: '#0e67a2',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            {getText('createNewBtn')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: EMZI_SPACING.xl, textAlign: 'center' }}>
        <h1 style={{ fontSize: '22px', fontWeight: '700', marginBottom: EMZI_SPACING.sm, color: '#fff' }}>
          📝 {getText('title')}
        </h1>
        <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)' }}>{getText('subtitle')}</p>
      </div>

      {/* Script Summary */}
      {script && (
        <Card style={{ marginBottom: EMZI_SPACING.lg, borderRadius: EMZI_RADIUS.md }}>
          <CardHeader>
            <CardTitle style={{ fontSize: '16px', fontWeight: '600' }}>
              🎬 {getText('scriptSummary')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
              <div style={{ padding: '12px', background: '#E8F4FF', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#0e67a2' }}>{script.platform}</div>
                <div style={{ fontSize: '11px', color: '#666' }}>Platform</div>
              </div>
              <div style={{ padding: '12px', background: '#E6F9E6', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#28a745' }}>{script.duration_minutes}m</div>
                <div style={{ fontSize: '11px', color: '#666' }}>Duration</div>
              </div>
              <div style={{ padding: '12px', background: '#FFF5E6', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#F89B29' }}>{script.sections?.length || 0}</div>
                <div style={{ fontSize: '11px', color: '#666' }}>{getText('sections')}</div>
              </div>
              <div style={{ padding: '12px', background: '#FCE4EC', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#e91e63' }}>{script.total_ctas}</div>
                <div style={{ fontSize: '11px', color: '#666' }}>{getText('ctas')}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* SOA Summary */}
      <Card style={{ marginBottom: EMZI_SPACING.lg, borderRadius: EMZI_RADIUS.md }}>
        <CardHeader>
          <CardTitle style={{ fontSize: '16px', fontWeight: '600' }}>
            🎯 {getText('soaSummary')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
            <div style={{ padding: '16px', background: '#E8F4FF', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#0e67a2' }}>{soaStats.avgScore}</div>
              <div style={{ fontSize: '12px', color: '#666' }}>{getText('avgScore')}</div>
            </div>
            <div style={{ padding: '16px', background: '#E6F9E6', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#28a745' }}>{soaStats.passed}/{soaStats.total}</div>
              <div style={{ fontSize: '12px', color: '#666' }}>{getText('passed')}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Project Name */}
      <Card style={{ marginBottom: EMZI_SPACING.lg, borderRadius: EMZI_RADIUS.md }}>
        <CardHeader>
          <CardTitle style={{ fontSize: '16px', fontWeight: '600' }}>
            {getText('projectNameLabel')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <input
            type="text"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            placeholder={getText('projectNamePlaceholder')}
            style={{
              width: '100%',
              padding: '14px',
              border: '2px solid #e0e0e0',
              borderRadius: '10px',
              fontSize: '14px'
            }}
          />
        </CardContent>
      </Card>

      {/* Notes */}
      <Card style={{ marginBottom: EMZI_SPACING.lg, borderRadius: EMZI_RADIUS.md }}>
        <CardHeader>
          <CardTitle style={{ fontSize: '16px', fontWeight: '600' }}>
            {getText('notesLabel')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={getText('notesPlaceholder')}
            rows={3}
            style={{
              width: '100%',
              padding: '14px',
              border: '2px solid #e0e0e0',
              borderRadius: '10px',
              fontSize: '14px',
              resize: 'vertical'
            }}
          />
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
        <button
          onClick={onBack}
          style={{
            flex: 1,
            padding: '14px',
            background: '#fff',
            border: '2px solid #0e67a2',
            borderRadius: '10px',
            color: '#0e67a2',
            fontSize: '15px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          ← Back
        </button>
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          style={{
            flex: 2,
            padding: '14px',
            background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
            border: 'none',
            borderRadius: '10px',
            color: '#fff',
            fontSize: '15px',
            fontWeight: '700',
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            opacity: isSubmitting ? 0.7 : 1,
            boxShadow: '0 4px 15px rgba(40, 167, 69, 0.4)'
          }}
        >
          {isSubmitting ? getText('submittingBtn') : getText('submitBtn')}
        </button>
      </div>
    </div>
  );
};

export default Step10ReviewSubmit;
